/**
 * Pure decision function for whether the Rovo Insights entry-point banner is shown,
 * and — when it is not — the single reason why. Kept free of React/analytics so both
 * banner surfaces (For You tab, Rovo empty state) resolve visibility identically and
 * report the same `reason` taxonomy.
 */

export type InsightsStatusFetchState = 'disabled' | 'loading' | 'success' | 'error';

export type InsightsBannerSurface = 'yourWork' | 'quickActions';

/** The reasons that are actually reported. Follows the local `SLOExclusionReason` idiom. */
export const InsightsBannerReason = {
	SHOWN: 'shown',
	// Status fetch never ran.
	NO_PRODUCT: 'noProduct',
	OFF_SCHEDULE: 'offSchedule',
	// Status fetch ran but the banner is still suppressed.
	FETCH_ERROR: 'fetchError',
	STATUS_UNAVAILABLE: 'statusUnavailable',
	SPACE_SELECTED: 'spaceSelected',
	STALE: 'stale',
} as const;

export type InsightsBannerReason = (typeof InsightsBannerReason)[keyof typeof InsightsBannerReason];

export interface InsightsBannerResolution {
	shown: boolean;
	/**
	 * `null` while the decision is not reportable — the user is outside the experiment,
	 * or the status fetch has not settled (disabled / in flight). Callers should not report.
	 */
	reason: InsightsBannerReason | null;
}

export interface ResolveInsightsBannerVisibilityInput {
	/** User is in the pulse experiment. */
	pulseEnabled: boolean;
	/** Surface suppresses the banner when the host product is unknown (For You tab does; empty state does not). */
	requiresProduct: boolean;
	hasProduct: boolean;
	/** Within the Mon–Tue schedule, or the schedule is being ignored via gate. */
	scheduleOk: boolean;
	statusFetchState: InsightsStatusFetchState;
	isInsightsAvailable: boolean;
	/** Surface only shows fresh insights (quick-actions tab); yourWork shows any available. */
	requiresFresh: boolean;
	isFresh: boolean;
	hasSelectedSpace: boolean;
}

/**
 * Reasons are evaluated in the same order the real render gates suppress the banner,
 * so the returned `reason` is the first gate that failed. A `null` reason means the
 * decision is not reportable yet (outside the experiment, or fetch not settled).
 */
export const resolveInsightsBannerVisibility = (
	input: ResolveInsightsBannerVisibilityInput,
): InsightsBannerResolution => {
	if (!input.pulseEnabled) {
		return { shown: false, reason: null };
	}
	if (input.requiresProduct && !input.hasProduct) {
		return { shown: false, reason: InsightsBannerReason.NO_PRODUCT };
	}
	if (!input.scheduleOk) {
		return { shown: false, reason: InsightsBannerReason.OFF_SCHEDULE };
	}
	if (input.statusFetchState === 'disabled' || input.statusFetchState === 'loading') {
		return { shown: false, reason: null };
	}
	if (input.statusFetchState === 'error') {
		return { shown: false, reason: InsightsBannerReason.FETCH_ERROR };
	}
	if (!input.isInsightsAvailable) {
		return { shown: false, reason: InsightsBannerReason.STATUS_UNAVAILABLE };
	}
	if (input.hasSelectedSpace) {
		return { shown: false, reason: InsightsBannerReason.SPACE_SELECTED };
	}
	if (input.requiresFresh && !input.isFresh) {
		return { shown: false, reason: InsightsBannerReason.STALE };
	}
	return { shown: true, reason: InsightsBannerReason.SHOWN };
};
